import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { LOGO, COMPANY } from "@/lib/assets";

const NAV = [
  {
    label: "About Us",
    type: "dropdown",
    items: [
      { label: "About Company", to: "/about#company" },
      { label: "Board Of Directors", to: "/about#board" },
      { label: "Board Committees", to: "/about#committees" },
    ],
  },
  { label: "Materials", to: "/#materials" },
  { label: "Supply Chain", to: "/#supply" },
  { label: "Journal", to: "/blog" },
  {
    label: "Investor Relations",
    type: "dropdown",
    items: [
      { label: "IPO", to: "/investors#ipo" },
      { label: "Annual Report", to: "/investors#annual-report" },
      { label: "Financial Results", to: "/investors#financial-results" },
      { label: "Policies", to: "/investors#policies" },
      { label: "MOA And AOA", to: "/investors#moa-aoa" },
      { label: "Material Contracts & Documents", to: "/investors#contracts" },
    ],
  },
  { label: "Contact", to: "/#contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false); // mobile
  const [openMenu, setOpenMenu] = useState(null); // desktop dropdown label
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (e, to) => {
    e.preventDefault();
    setOpen(false);
    setOpenMenu(null);
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 300);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
      return;
    }
    if (to.includes("#")) {
      const [path, hash] = to.split("#");
      if (location.pathname !== path) {
        navigate(to);
      } else {
        document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
      }
      return;
    }
    navigate(to);
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        scrolled ? "bg-background/90 backdrop-blur-md border-b border-border py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          <img src={LOGO} alt={COMPANY.name} className="h-9 md:h-10 w-auto object-contain" />
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((l) =>
            l.type === "dropdown" ? (
              <div
                key={l.label}
                className="relative"
                onMouseEnter={() => setOpenMenu(l.label)}
                onMouseLeave={() => setOpenMenu(null)}
              >
                <button
                  data-testid={`nav-menu-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  className="font-mono2 text-xs uppercase tracking-[0.15em] text-foreground link-underline inline-flex items-center gap-1"
                  onClick={() => setOpenMenu(openMenu === l.label ? null : l.label)}
                >
                  {l.label} <ChevronDown size={12} className={`transition-transform ${openMenu === l.label ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {openMenu === l.label && (
                    <motion.div
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.2 }}
                      className="absolute left-0 top-full pt-3 min-w-[260px]"
                      data-testid={`nav-menu-panel-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                    >
                      <div className="bg-background border border-border shadow-lg py-3">
                        {l.items.map((it) => (
                          <a
                            key={it.label}
                            href={it.to}
                            onClick={(e) => goTo(e, it.to)}
                            data-testid={`nav-item-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            className="block px-5 py-2.5 text-sm hover:bg-muted hover:text-primary transition-colors"
                          >
                            {it.label}
                          </a>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <a
                key={l.label}
                href={l.to}
                onClick={(e) => goTo(e, l.to)}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className="font-mono2 text-xs uppercase tracking-[0.15em] text-foreground link-underline"
              >
                {l.label}
              </a>
            )
          )}
          <a
            href="/#contact"
            onClick={(e) => goTo(e, "/#contact")}
            data-testid="nav-cta"
            className="bg-foreground text-background px-5 py-2.5 font-mono2 text-xs uppercase tracking-[0.15em] hover:bg-primary transition-colors duration-300"
          >
            Get a Quote
          </a>
        </nav>

        <button
          className="lg:hidden text-foreground"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-background border-t border-border mt-3 max-h-[75vh] overflow-y-auto" data-testid="nav-mobile-menu">
          <div className="flex flex-col px-6 py-4">
            {NAV.map((l) =>
              l.type === "dropdown" ? (
                <div key={l.label} className="py-3 border-b border-border/60">
                  <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-primary mb-2">{l.label}</p>
                  <div className="flex flex-col gap-2 pl-1">
                    {l.items.map((it) => (
                      <a
                        key={it.label}
                        href={it.to}
                        onClick={(e) => goTo(e, it.to)}
                        className="text-sm py-1"
                      >
                        {it.label}
                      </a>
                    ))}
                  </div>
                </div>
              ) : (
                <a
                  key={l.label}
                  href={l.to}
                  onClick={(e) => goTo(e, l.to)}
                  className="py-3 font-mono2 text-sm uppercase tracking-[0.15em] border-b border-border/60"
                >
                  {l.label}
                </a>
              )
            )}
          </div>
        </div>
      )}
    </header>
  );
};
